/**
 * The follow-the-cursor preview of the building you are about to place.
 *
 * Build mode used to show a single 16px corner bracket on the hovered tile and
 * nothing else: you armed a Farmhouse, saw a green square a fraction of its size,
 * clicked, and only then learned how big it was, where it sat relative to the
 * fence you just laid, and that it would take an hour. All three of those are
 * decisions, and all three were being made blind.
 *
 * So the ghost draws the REAL sprite, at the real scale, anchored exactly where the
 * building will land, tinted grey-blue when the tile is legal and red when it is
 * not — plus a caption carrying the cost and the build time, and, when the tile is
 * refused, the reason. The recipe (tint 0x7f92aa, alpha 0.55) is the same one
 * scaffolding uses in plots.ts, so a ghost and an actual under-construction
 * building read as the same "not here yet" state.
 */
import { Container, Sprite, Text, Texture, Assets } from 'pixi.js';
import { CANVAS_FONT } from '../../fonts';
import { TILE } from '../../map/world';
import { BUILDINGS, type FarmCtx } from './context';
import { buildingScale, buildingZ } from './plots';
import { TENT_KIND, TENT_SCALE, TENT_ANCHOR_Y } from './placement';

/** Ghost look per state. Pure, so the valid/invalid contract is testable. */
export function ghostStyle(ok: boolean): { tint: number; alpha: number } {
  // Red is not a tint of the art here but a wash over it: at 0.55 alpha a pure red
  // multiply on dark pack sprites is nearly black, which reads as a shadow rather
  // than as a refusal, so the invalid state is both redder AND more opaque.
  return ok ? { tint: 0x7f92aa, alpha: 0.55 } : { tint: 0xff5555, alpha: 0.7 };
}

/** The caption under the ghost: what it costs and how long it takes — or why not. */
export function ghostCaption(kind: string, blocker: string | null): string {
  // The tent is not a building (no cost, no timer, you already own it), so the
  // caption states what it BUYS instead: mobs leave you alone inside its ring.
  if (kind === TENT_KIND) return blocker ? `Tent — ${blocker}` : 'Tent · safe camp · free to move';
  const def = BUILDINGS[kind];
  if (!def) return blocker ?? '';
  const cost = [def.wood ? `${def.wood} wood` : '', def.stone ? `${def.stone} stone` : ''].filter(Boolean).join(' · ');
  const time = def.buildMin ? `~${def.buildMin >= 60 ? `${Math.round(def.buildMin / 60)}h` : `${def.buildMin}m`} to build` : 'instant';
  return blocker ? `${def.name} — ${blocker}` : [def.name, cost, time].filter(Boolean).join(' · ');
}

/**
 * Where a placeable's ghost art lives.
 *
 * The tent is the one placeable that is NOT a building, so its art does not live in
 * /sprites/buildings. That path 404s for it, the loader swallows the failure, and
 * the result was a tent you carried around as an empty space with a caption under
 * it — the one placement where seeing the footprint matters most, because it
 * decides where your safe ground is.
 */
export function ghostSpriteUrl(kind: string): string {
  return kind === TENT_KIND ? '/sprites/props/tent_small.png' : `/sprites/buildings/${kind}.png`;
}

/**
 * How big the ghost draws — the SAME number the placed thing will use.
 *
 * A preview whose size is decided independently of the renderer is a preview that
 * lies, and this one did: the tent's art is a 48x96 building cell rather than
 * 16px pack art, so `buildingScale` sized the ghost at TILE/16 x 1.2 = 3.6 while
 * scene.ts pitched the real tent at 1.725 — the ghost was more than twice the
 * tent you got. Both now read one constant.
 */
export const ghostScale = (kind: string): number =>
  (kind === TENT_KIND ? TENT_SCALE : buildingScale(kind));

/**
 * Where the art is pinned inside its tile. Buildings sit on the tile's bottom
 * edge; the tent is pinned a touch higher (see TENT_ANCHOR_Y), and the ghost has
 * to match or it previews the tent low.
 */
export const ghostAnchorY = (kind: string): number => (kind === TENT_KIND ? TENT_ANCHOR_Y : 1);

const ghostTex = new Map<string, Texture>();

/**
 * Show (or move) the ghost. Called every pointer move while build mode is armed.
 *
 * The container is created once per farm view and re-textured on kind changes, so a
 * hover is a couple of property writes — this runs on every mousemove.
 */
export function updateBuildGhost(c: FarmCtx, kind: string, tx: number, ty: number, blocker: string | null): void {
  const layer = c.entityLayer ?? c.plotLayer;
  if (!layer) return;
  let g = c.buildGhost;
  // buildScene() wipes the stage and mints a NEW entityLayer (a plot upgrade or a
  // tutorial exit re-runs it mid-session), which destroys/orphans the ghost while
  // this cached reference stays non-null. Without this it would never re-render
  // again — the headline preview silently disappears until a reload.
  if (g && (g.destroyed || g.parent !== layer)) { c.buildGhost = null; g = null; }
  if (!g) {
    g = new Container();
    const sprite = new Sprite(Texture.EMPTY);
    sprite.anchor.set(0.5, 1);
    const label = new Text({
      text: '',
      style: { fontFamily: CANVAS_FONT, fontSize: 9, fill: 0xffe9b8, fontWeight: 'bold', align: 'center', stroke: { color: 0x1c150c, width: 3 } },
    });
    label.anchor.set(0.5, 0);
    g.addChild(sprite, label);
    g.eventMode = 'none';        // the ghost must never eat the click that places it
    layer.addChild(g);
    c.buildGhost = g;
  }
  const [sprite, label] = g.children as [Sprite, Text];

  if (g.label !== kind) {
    // `Container.label` is a free string slot — used here to remember which kind the
    // sprite is currently showing, so the texture is only swapped when it changes.
    g.label = kind;
    // The tent is the one placeable that is NOT a building, so its art does not
    // live in /sprites/buildings. That path 404s for it, the loader below swallows
    // the failure, and the result was a tent you carried around as an empty space
    // with a caption under it — the one placement where seeing the footprint
    // matters most, because it decides where your safe ground is.
    const url = ghostSpriteUrl(kind);
    const cached = ghostTex.get(kind);
    if (cached) sprite.texture = cached;
    else {
      void Assets.load(url).then((t: Texture) => {
        ghostTex.set(kind, t);
        // The player may have armed something else (or left build mode) during the
        // load — applying a stale texture would show the wrong building.
        if (!sprite.destroyed && c.buildGhost === g && g.label === kind) sprite.texture = t;
      }).catch(() => { /* missing sprite: the caption still says what it is */ });
    }
    sprite.scale.set(ghostScale(kind));
    sprite.anchor.set(0.5, ghostAnchorY(kind));
  }

  const style = ghostStyle(!blocker);
  sprite.tint = style.tint;
  sprite.alpha = style.alpha;
  label.text = ghostCaption(kind, blocker);
  label.y = 6;                                   // just below the tile's bottom edge
  g.x = tx * TILE + TILE / 2;
  g.y = (ty + ghostAnchorY(kind)) * TILE;
  // Sorted exactly as the finished building will be, so the preview is occluded by
  // the same trees and hidden behind the same rows as the real thing.
  g.zIndex = buildingZ(tx, ty) + 0.4;
  g.visible = true;
}

/** Take the ghost off the map (build mode left, or the pointer went off-world). */
export function hideBuildGhost(c: FarmCtx): void {
  if (c.buildGhost) c.buildGhost.visible = false;
}

/** Destroy it for good — the farm view is going away. */
export function destroyBuildGhost(c: FarmCtx): void {
  c.buildGhost?.destroy({ children: true });
  c.buildGhost = null;
}
